import axios, { AxiosResponse } from 'axios';
import { ApiResponse, EquipState, GetReportResponse, zps_ea_war, zps_wp } from '../zps';
import { handleDownload } from '../../app/utils/script';
import { PeriodTotals } from '../dashboardV2';

export type IEquipment = {
  almostFullPerc: number;
  almostFullValue: number;
  bag: boolean;
  bankid: number;
  cardInsertionTimeout: number;
  cardRemovalTimeout: number;
  comuid: number;
  currencies: IEquipmentCurrency[];
  dataInsertionTimeout: number;
  declarationTimeout: number;
  depositTimeout: number;
  description: string;
  eid: number;
  entid: number;
  equiId: number;
  etid: number;
  etvid: number;
  fullPerc: number;
  fullValue: number;
  fullValueCid: number;
  gid: number;
  idTerminal: number;
  inuse: number;
  lang: number;
  languages: IEquipmentLanguage[];
  lid: number;
  maintid: number;
  maintid2: number;
  maxBills: number;
  maxCoins: number;
  maxCurrencies: number;
  p43sf8: number;
  region: string;
  sa1: number;
  sa2: number;
  sa3: number;
  sa4: number;
  sa5: number;
  sa6: number;
  sa7: number;
  sa8: number;
  secur: number;
  serialnumber: string;
  sid: number;
  status: number;
  suppid: number;
  token: string;
  zkey: string;
  amount: string;
  lat?: number;
  lng?: number;
};

export type IEquipmentState = {
  cardreaderState: number;
  depositToRecover: boolean;
  diskUsage: number;
  equipmentState: number;
  fakeNoteDetected: boolean;
  moosState: number;
  paySystemState: number;
  printerState: number;
  quantityState: number;
  switchs: number;
  upsState: number;
  valueState: number;
};

export type IEquipmentLanguage = {
  eid: number;
  gid: number;
  langid: number;
  langidx: number;
};

export type IEditEquipmentCurrencyRequest = {
  eid: number;
  currency: IEquipmentCurrency;
};

export type IEquipmentDenomination = {
  existRecycler?: number;
  existSafe?: number;
  existTotal?: number;
  index: number;
  type?: number;
  value?: number;
  wc: number;
  wcLacking?: number;
  wcSurplus?: number;
  weight?: number;
};

export type IEquipmentCurrency = {
  cid: number;
  eid?: number;
  gid?: number;
  maxAmount?: number;
  minAmount?: number;
  denominations?: IEquipmentDenomination[];
  description?: string;
  factor?: number;
};

export type IEntityLabel = {
  entid?: number;
  name?: string;
  label?: string;
};

export type IEquipmentDetails = {
  equipment: IEquipment;
  equipmentState: IEquipmentState;
  lastComDt: string;
  lastLocalIp: string;
  lastOperation: string;
  lastOperationDt: string;
  lastCommIp?: string;
  period: number;
  periodAmounts?: PeriodTotals[];
  softwareVersion: string;
  safeOccPerc?: number;
  location?: string;
  amount?: string;
  position?: google.maps.LatLngLiteral;
  inService: boolean;
  status: number;
  statusList?: IEquipmentDeviceStatus[];
  workingState: EquipState;
};

export type IEquipmentOption = {
  eid: number;
  label: string;
};

export enum EquipmentDeviceStatusLevel {
  OK = 'OK',
  WARNING = 'WARNING',
  ERROR = 'ERROR'
}

export type IEquipmentDeviceStatus = {
  id: string;
  idLangKey: string;
  level: EquipmentDeviceStatusLevel;
  statusLangKey: string;
};

export type IRemoteOperation = {
  eid: number;
  operation: string;
};

export interface IEquipmentStatusResponse extends ApiResponse {
  status: IEquipmentDetails;
}

export interface IEquipmentDetailsResponse extends ApiResponse {
  equipments: IEquipmentDetails[];
}

export interface IEquipmentListResponse extends ApiResponse {
  equipments: IEquipmentDetails[];
  totalRows: number;
  type: string;
}

export interface IEquipmentOptionsResponse extends ApiResponse {
  list: IEquipmentOption[];
  type: string;
}

export interface GetEquipmentCurrencyResponse extends ApiResponse {
  list: IEquipmentCurrency[];
  type: string;
  totalRows: number;
}

export interface IHelpdeskEquipmentResponse extends ApiResponse {
  list: IEquipmentDetails[];
  totalRows: number;
  type: string;
}

export type Reason = {
  gid: number;
  entid: number;
  langid: number;
  code: number;
  reason: string;
};

export type Declaration = {
  gid: number;
  entid: number;
  langid: number;
  code: number;
  declaration: string;
};

export interface EquipmentReasonsResponse extends ApiResponse {
  reasons: Reason[];
  type: string;
}

export interface EquipmentDeclarationResponse extends ApiResponse {
  declarations: Declaration[];
  type: string;
}

export interface AddEquipmentRequest {
  entid: number;
  equipmentFile: string;
}

export interface EquipmentUpdateRequest {
  almostFullPerc?: number;
  almostFullValue?: number;
  almostFullValueToShow?: number;
  amounts?: [];
  bankid?: number;
  cardInsertionTimeout?: number;
  cardRemovalTimeout?: number;
  currencies?: [];
  dataInsertionTimeout?: number;
  declarationTimeout?: number;
  depositTimeout?: number;
  description: string;
  eid?: number;
  entid?: number;
  fullPerc?: number;
  fullValue?: number;
  fullValueCid?: number;
  fullValueCidObj?: number;
  fullValueToShow?: number;
  lang: number;
  languages?: [];
  lastComDt?: string;
  lastOperationDt?: string;
  maxBills?: number;
  maxCoins?: number;
  period?: string;
  region?: string;
  sid?: number;
  status?: string;
  type?: number;
  _status?: number;
}

export const fetchEquipmentList = (
  controller: AbortController,
  entid: number,
  sid: number,
  offset: number,
  limit: number,
  totalRows: boolean
): Promise<AxiosResponse<IEquipmentListResponse>> => {
  return axios.get<IEquipmentListResponse>(`${zps_wp}/api/equipment/details`, {
    params: { entid, sid, offset, limit, totalRows },
    signal: controller.signal
  });
};

export const updateEquipment = (
  request: EquipmentUpdateRequest,
  controller: AbortController
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.put<ApiResponse>(`${zps_wp}/api/equipment`, request, {
    signal: controller.signal
  });
};

export const fetchEquipmentStatus = (eid: number): Promise<AxiosResponse<IEquipmentStatusResponse>> => {
  return axios.get<IEquipmentStatusResponse>(`${zps_wp}/api/equipment/status`, {
    params: { eid }
  });
};

export const fetchEquipmentOptions = (
  entid: number,
  sid: number,
  controller: AbortController
): Promise<AxiosResponse<IEquipmentOptionsResponse>> => {
  return axios.get<IEquipmentOptionsResponse>(`${zps_wp}/api/equipment/options`, {
    params: { entid, sid },
    signal: controller.signal
  });
};

export const fetchEquipmentCurrency = (
  controller: AbortController,
  eid: number
): Promise<AxiosResponse<GetEquipmentCurrencyResponse>> => {
  return axios.get<GetEquipmentCurrencyResponse>(`${zps_wp}/api/equipment/currency`, {
    params: { eid },
    signal: controller.signal
  });
};

export const fetchEquipmentDetails = (
  controller: AbortController,
  eid: number
): Promise<AxiosResponse<IEquipmentDetailsResponse>> => {
  return axios.get<IEquipmentDetailsResponse>(`${zps_wp}/api/equipment/details`, {
    params: { eid },
    signal: controller.signal
  });
};

export const addEquipment = (
  request: AddEquipmentRequest,
  controller: AbortController
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.post<ApiResponse>(`${zps_wp}/api/equipment/addbyfile`, request, {
    signal: controller.signal
  });
};

export const updateEquipmentWorkingCapital = (
  controller: AbortController,
  payload: IEditEquipmentCurrencyRequest
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.put<ApiResponse>(`${zps_ea_war}/equipment/currency`, payload, {
    signal: controller.signal
  });
};

export const equipmentRemoteOperation = (
  controller: AbortController,
  payload: IRemoteOperation
): Promise<AxiosResponse<ApiResponse>> => {
  return axios.post<ApiResponse>(`${zps_wp}/api/equipment/remoteoperation`, payload, {
    signal: controller.signal
  });
};

/* -------------------- REPORTS (WITH DOWNLOAD) -------------------- */

export const fetchExistencesReport = (
  eid: number,
  period: number,
  controller: AbortController
): Promise<AxiosResponse<GetReportResponse>> => {
  return axios
    .get<GetReportResponse>(`${zps_ea_war}/equipment/details/report/existences/`, {
      params: { eid, period },
      signal: controller.signal
    })
    .then(res => {
      if (res.data?.statusOper?.code === 0 && res.data.report) {
        handleDownload(res.data.report, res.data.name, res.data.mime);
      }
      return res;
    });
};

export const fetchEquipmentCurrencyReportXLS = (
  controller: AbortController,
  eid: number
): Promise<AxiosResponse<GetReportResponse>> => {
  return axios
    .get<GetReportResponse>(`${zps_wp}/api/equipment/currency/report`, {
      params: { format: 'xls', eid },
      signal: controller.signal
    })
    .then(res => {
      if (res.data?.statusOper?.code === 0 && res.data.report) {
        handleDownload(res.data.report, res.data.name, res.data.mime);
      }
      return res;
    });
};
